import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ComplianceDocument, ComplianceStatus, ComplianceTenantSettings } from '@cloudsign/shared';
import type { DocumentType } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, Spinner, TextField } from '../../components/ui';
import { UploadWithProgress } from '../../components/phase3';
import { COMPLIANCE, formatDay } from './api';
import { useDocumentTypes } from './ComplianceTypesPage';

/** GET /compliance/members/:memberId (contract compliance s4). */
export const MemberCompliance = z.object({
  status: ComplianceStatus,
  documents: z.array(ComplianceDocument),
});
export type MemberCompliance = z.infer<typeof MemberCompliance>;

const UploadAnswer = z.object({ uploadId: z.uuid() });
const MAX_MB = ComplianceTenantSettings.parse({}).maxDocumentMb;

type Tone = 'neutral' | 'good' | 'warn' | 'bad';
export const STATE_TONE: Record<string, Tone> = {
  valid: 'good',
  expiring: 'warn',
  expired: 'bad',
  missing: 'bad',
  pending_review: 'warn',
  rejected: 'bad',
  superseded: 'neutral',
};

function statusKey(state: string): string {
  return `w18.doc.state.${state}`;
}

function AddDocument({ memberId, types, onDone }: { memberId: string; types: DocumentType[]; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [typeId, setTypeId] = useState(types[0]?.id ?? '');
  const [reference, setReference] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [problem, setProblem] = useState('');
  const type = types.find((x) => x.id === typeId);
  const add = useMutation({
    mutationFn: () =>
      api.post(`${COMPLIANCE}/members/${memberId}/documents`, {
        body: {
          typeId,
          uploadId,
          reference,
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
        },
        schema: ComplianceDocument,
      }),
    onSuccess: async () => {
      toast(t('w18.doc.added'));
      await qc.invalidateQueries({
        queryKey: ['w18', 'compliance', 'member', memberId],
      });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem('');
    if (type?.needsFile && !uploadId) return setProblem(t('w18.doc.needFile'));
    if (type?.needsExpiry && !expiryDate) return setProblem(t('w18.doc.needExpiry'));
    if (type?.needsIssueDate && !issueDate) return setProblem(t('w18.doc.needIssue'));
    add.mutate();
  };
  return (
    <Card title={t('w18.doc.add')}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <div>
          <label htmlFor="w18-doc-type" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.doc.type')}
          </label>
          <select
            id="w18-doc-type"
            className="block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
            value={typeId}
            onChange={(e) => {
              setTypeId(e.target.value);
              setUploadId(null);
              setFileName('');
            }}
          >
            {types.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          {type?.description ? <p className="mt-1 text-xs text-slate-600">{type.description}</p> : null}
        </div>
        {type?.needsFile ? (
          <div>
            <UploadWithProgress
              label={t('w18.doc.file')}
              url={`/api/v1${COMPLIANCE}/uploads`}
              maxBytes={MAX_MB * 1024 * 1024}
              accept={['application/pdf', 'image/jpeg', 'image/png']}
              hint={t('w18.doc.fileHint', { max: MAX_MB })}
              onDone={(res, file) => {
                const parsed = UploadAnswer.safeParse(res);
                if (parsed.success) {
                  setUploadId(parsed.data.uploadId);
                  setFileName(file.name);
                } else setProblem(t('p3w.upload.failed'));
              }}
              onError={() => setUploadId(null)}
            />
            {fileName ? <p className="mt-1 text-xs text-slate-600">{t('w18.doc.attached', { name: fileName })}</p> : null}
          </div>
        ) : null}
        <TextField label={t('w18.doc.reference')} maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} />
        <div className="grid gap-4 sm:grid-cols-2">
          {type?.needsIssueDate ? <TextField type="date" label={t('w18.doc.issueDate')} required value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /> : null}
          {type?.needsExpiry ? <TextField type="date" label={t('w18.doc.expiryDate')} required value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /> : null}
        </div>
        {problem ? <Alert>{problem}</Alert> : null}
        <ErrorLine error={add.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onDone}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={add.isPending || !typeId}>
            {t('w18.doc.addButton')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Review({ doc, memberId }: { doc: ComplianceDocument; memberId: string }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const review = useMutation({
    mutationFn: (body: { decision: 'valid' | 'rejected'; reason?: string }) =>
      api.post(`${COMPLIANCE}/documents/${doc.id}/review`, {
        body,
        schema: ComplianceDocument,
      }),
    onSuccess: async () => {
      setRejecting(false);
      await qc.invalidateQueries({
        queryKey: ['w18', 'compliance', 'member', memberId],
      });
    },
  });
  if (rejecting) {
    return (
      <form
        className="mt-2 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          review.mutate({ decision: 'rejected', reason });
        }}
      >
        <TextField label={t('w18.doc.rejectReason')} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="min-w-56 flex-1" />
        <Button type="submit" variant="danger" disabled={review.isPending || reason.trim() === ''}>
          {t('w18.doc.reject')}
        </Button>
        <Button onClick={() => setRejecting(false)}>{t('common.cancel')}</Button>
        <ErrorLine error={review.error} />
      </form>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <Button variant="primary" disabled={review.isPending} onClick={() => review.mutate({ decision: 'valid' })} aria-label={t('w18.doc.acceptName', { name: doc.typeName })}>
        {t('w18.doc.accept')}
      </Button>
      <Button onClick={() => setRejecting(true)} aria-label={t('w18.doc.rejectName', { name: doc.typeName })}>
        {t('w18.doc.reject')}
      </Button>
      <ErrorLine error={review.error} />
    </div>
  );
}

/**
 * Member drawer tab "Documents" (contract compliance s6): standing against the group's requirements, the documents held
 * (current and history), upload and review. Exported for W23 to mount in the member drawer.
 */
export function MemberDocumentsTab({ memberId }: { memberId: string }) {
  const canManage = useCan('compliance.manage');
  const types = useDocumentTypes();
  const [adding, setAdding] = useState(false);
  const q = useQuery({
    queryKey: ['w18', 'compliance', 'member', memberId],
    queryFn: () =>
      api.get(`${COMPLIANCE}/members/${memberId}`, {
        schema: MemberCompliance,
      }),
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const { status, documents } = q.data;
  const current = documents.filter((d) => d.status !== 'superseded');
  const history = documents.filter((d) => d.status === 'superseded');
  const usable = (types.data ?? []).filter((d) => d.active);
  return (
    <div className="space-y-5">
      <section aria-labelledby="w18-doc-standing">
        <h3 id="w18-doc-standing" className="mb-2 text-sm font-semibold text-slate-900">
          {t('w18.doc.standing')}
        </h3>
        <p className="mb-2">
          <Badge tone={status.outcome === 'met' ? 'good' : status.outcome === 'warn' ? 'warn' : status.outcome === 'refuse' ? 'bad' : 'neutral'}>
            {t(`w18.doc.outcome.${status.outcome}`)}
          </Badge>
        </p>
        {status.items.length === 0 ? (
          <p className="text-sm text-slate-600">{t('w18.doc.noRequirements')}</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {status.items.map((i) => (
              <li key={i.typeId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span>
                  {i.typeName} <span className="text-xs text-slate-600">({t(`w18.compGroup.mode.${i.mode}`)})</span>
                </span>
                <span className="flex items-center gap-2">
                  {i.expiryDate ? <span className="text-xs text-slate-600">{formatDay(i.expiryDate)}</span> : null}
                  <Badge tone={STATE_TONE[i.state] ?? 'neutral'}>{t(statusKey(i.state))}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
        {status.induction.required ? (
          <p className="mt-2 text-sm text-slate-700">
            {t('w18.doc.induction')}: <Badge tone={status.induction.state === 'valid' ? 'good' : 'bad'}>{t(`w18.doc.induction.${status.induction.state}`)}</Badge>
            {status.induction.passedUntil ? (
              <span className="ml-2 text-xs text-slate-600">
                {t('w18.doc.until', {
                  date: formatDateTime(status.induction.passedUntil),
                })}
              </span>
            ) : null}
          </p>
        ) : null}
      </section>

      <section aria-labelledby="w18-doc-held">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 id="w18-doc-held" className="text-sm font-semibold text-slate-900">
            {t('w18.doc.held')}
          </h3>
          {canManage && !adding ? (
            <Button onClick={() => setAdding(true)} disabled={usable.length === 0}>
              {t('w18.doc.add')}
            </Button>
          ) : null}
        </div>
        {adding ? <AddDocument memberId={memberId} types={usable} onDone={() => setAdding(false)} /> : null}
        {current.length === 0 ? (
          <EmptyState message={t('w18.doc.none')} />
        ) : (
          <ul className="space-y-3">
            {current.map((d) => (
              <li key={d.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-slate-900">{d.typeName}</span>
                  <Badge tone={STATE_TONE[d.status] ?? 'neutral'}>{t(statusKey(d.status))}</Badge>
                </div>
                <dl className="mt-1 grid gap-x-4 text-xs text-slate-600 sm:grid-cols-2">
                  {d.reference ? <div>{t('w18.doc.referenceIs', { value: d.reference })}</div> : null}
                  {d.issueDate ? <div>{t('w18.doc.issuedOn', { date: formatDay(d.issueDate) })}</div> : null}
                  {d.expiryDate ? (
                    <div>
                      {t('w18.doc.expiresOn', {
                        date: formatDay(d.expiryDate),
                      })}
                    </div>
                  ) : null}
                  <div>
                    {t('w18.doc.uploadedBy', {
                      who: d.uploadedByLabel,
                      via: t(`w18.doc.via.${d.uploadedVia}`),
                    })}
                  </div>
                  {d.reviewedByLabel ? (
                    <div>
                      {t('w18.doc.reviewedBy', {
                        who: d.reviewedByLabel,
                        when: formatDateTime(d.reviewedAt),
                      })}
                    </div>
                  ) : null}
                </dl>
                {d.rejectReason ? <p className="mt-1 text-xs text-red-800">{t('w18.doc.rejectedBecause', { reason: d.rejectReason })}</p> : null}
                {d.fileId ? (
                  <a
                    className="mt-2 inline-block text-sm font-medium text-brand underline"
                    href={`/api/v1${COMPLIANCE}/documents/${d.id}/file`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('w18.doc.view')}
                    <span className="sr-only"> {d.typeName}</span>
                  </a>
                ) : null}
                {canManage && d.status === 'pending_review' ? <Review doc={d} memberId={memberId} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {history.length > 0 ? (
        <section aria-labelledby="w18-doc-history">
          <h3 id="w18-doc-history" className="mb-2 text-sm font-semibold text-slate-900">
            {t('w18.doc.history')}
          </h3>
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
            {history.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span>
                  {d.typeName}
                  {d.expiryDate ? (
                    <span className="ml-2 text-xs text-slate-600">
                      {t('w18.doc.expiresOn', {
                        date: formatDay(d.expiryDate),
                      })}
                    </span>
                  ) : null}
                </span>
                <span className="flex items-center gap-3">
                  <Badge>{t(statusKey(d.status))}</Badge>
                  {d.fileId ? (
                    <a className="text-sm font-medium text-brand underline" href={`/api/v1${COMPLIANCE}/documents/${d.id}/file`} target="_blank" rel="noopener noreferrer">
                      {t('w18.doc.view')}
                      <span className="sr-only"> {d.typeName}</span>
                    </a>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
